import { useMemo } from 'react';
import { RiCheckLine, RiDiscountPercentLine } from 'react-icons/ri';
import { colors } from '@/constants';
import {
    getCartItemComboStripStatusForGrupo,
    getComboStripMessage,
    getComboIdsForCartLine,
    resolveComboStripGruposForLine,
} from '@/talons/combos/comboCartUtils';
import { useComboCartItems } from '@/talons/combos/useComboCartItems';
import * as S from './styles';

type Props = {
    cartItemUid?: string;
    cartItemUids?: string[];
    cartItemSku?: number;
    cartItemOptionUid?: string;
    idCampanha?: string;
    comboInstanceId?: string;
    variant?: 'badge' | 'strip';
    comboPortion?: 'combo' | 'excess';
    displayQuantity?: number;
    comboDiscountQuantity?: number;
};

const SeloCombo = ({
    cartItemUid,
    cartItemUids,
    cartItemSku,
    cartItemOptionUid,
    idCampanha,
    comboInstanceId,
    variant = 'badge',
    comboPortion,
    displayQuantity,
    comboDiscountQuantity,
}: Props) => {
    const { getComboCartItemInfo, combos, cartItems, getComboInstanceStatusesById } = useComboCartItems();

    const resolvedCartItemUids = useMemo(
        () => (cartItemUids?.length ? cartItemUids : cartItemUid ? [cartItemUid] : []),
        [cartItemUid, cartItemUids]
    );

    const cartItemRef = useMemo(
        () =>
            cartItemSku != null && cartItemOptionUid
                ? { sku: cartItemSku, option_uid: cartItemOptionUid }
                : undefined,
        [cartItemSku, cartItemOptionUid]
    );

    const comboStripEntries = useMemo(() => {
        const allocations = resolvedCartItemUids.flatMap((uid) => getComboCartItemInfo(uid) ?? []);
        const comboIds = idCampanha
            ? [idCampanha]
            : getComboIdsForCartLine(allocations.length ? allocations : undefined, cartItemRef);

        return comboIds.flatMap((comboId) => {
            const combo = combos.find((c: { id_campanha?: string }) => c.id_campanha === comboId);
            if (!combo) return [];

            const grupoEntries = resolveComboStripGruposForLine(
                combo,
                allocations.length ? allocations : undefined,
                comboId,
                cartItemRef,
                comboInstanceId
            );

            return grupoEntries.map(({ grupo, grupoId, comboInstanceId: instanceId, lineAllocations }) => ({
                comboId,
                combo,
                grupo,
                grupoId,
                comboInstanceId: instanceId,
                lineAllocations,
            }));
        });
    }, [
        resolvedCartItemUids,
        cartItemRef,
        idCampanha,
        comboInstanceId,
        combos,
        getComboCartItemInfo,
    ]);

    if (!comboStripEntries.length) return null;

    if (variant === 'strip') {
        return (
            <S.Strip>
                {comboStripEntries.map(({ comboId, combo, grupo, grupoId, comboInstanceId: instanceId, lineAllocations }) => {
                    const primary = lineAllocations[0];
                    const groupStatus = getCartItemComboStripStatusForGrupo(
                        grupo,
                        comboId,
                        instanceId,
                        lineAllocations,
                        cartItems,
                        getComboCartItemInfo
                    );
                    const isGroupComplete = groupStatus.isGroupComplete;
                    const instanceStatuses = getComboInstanceStatusesById(comboId) ?? [];
                    const isInstanceComplete =
                        instanceStatuses.find((entry) => entry.comboInstanceId === instanceId)?.isComplete ??
                        false;
                    const accentColor = isGroupComplete ? colors.alert.green300 : colors.alert.orange300;
                    const comboGrupoTitle = `${groupStatus.comboName} — ${groupStatus.grupoLabel}`;
                    const statusLabel = isGroupComplete ? 'Grupo completo' : `Faltam ${groupStatus.remaining}`;
                    const message = getComboStripMessage(
                        {
                            ...combo,
                            nome_campanha: primary.nome_campanha,
                        },
                        groupStatus.grupoLabel,
                        { includeBenefit: isInstanceComplete }
                    );

                    return (
                        <S.StripBar
                            key={`${instanceId}-${grupoId}`}
                            $isActive={isGroupComplete}
                            title={`${comboGrupoTitle} — ${statusLabel}`}
                            aria-label={
                                isGroupComplete
                                    ? `${comboGrupoTitle} — ${statusLabel}`
                                    : `${comboGrupoTitle} — Faltam ${groupStatus.remaining} embalagens`
                            }
                        >
                            <S.StripIconBox $isActive={isGroupComplete}>
                                <RiDiscountPercentLine size={18} color={accentColor} />
                            </S.StripIconBox>

                            <S.StripText>
                                {message.prefix}
                                {message.highlight && (
                                    <S.StripHighlight $isActive={isGroupComplete}>
                                        {message.highlight}
                                    </S.StripHighlight>
                                )}
                                {message.suffix}
                            </S.StripText>

                            {isGroupComplete ? (
                                <S.StripStatusGroup>
                                    <S.StripCheck>
                                        <RiCheckLine size={20} color={colors.brand.grafite} />
                                    </S.StripCheck>
                                    <S.StripStatusLabel $isActive>Grupo completo</S.StripStatusLabel>
                                </S.StripStatusGroup>
                            ) : (
                                <S.StripStatusLabel>
                                    Faltam {groupStatus.remaining}
                                </S.StripStatusLabel>
                            )}
                        </S.StripBar>
                    );
                })}
            </S.Strip>
        );
    }

    return (
        <S.Wrapper>
            {comboStripEntries.map(({ comboId, grupo, grupoId, comboInstanceId: instanceId, lineAllocations }) => {
                const groupStatus = getCartItemComboStripStatusForGrupo(
                    grupo,
                    comboId,
                    instanceId,
                    lineAllocations,
                    cartItems,
                    getComboCartItemInfo
                );
                const isGroupComplete = groupStatus.isGroupComplete;
                const accentColor = isGroupComplete ? colors.alert.green300 : colors.alert.orange300;
                const comboGrupoTitle = `${groupStatus.comboName} — ${groupStatus.grupoLabel}`;
                const statusLabel = isGroupComplete ? 'Grupo completo' : `Faltam ${groupStatus.remaining}`;

                return (
                    <S.Badge
                        key={`${instanceId}-${grupoId}`}
                        $isActive={isGroupComplete}
                        title={`${comboGrupoTitle} — ${statusLabel}`}
                        aria-label={`${comboGrupoTitle} — ${statusLabel}`}
                    >
                        <RiDiscountPercentLine size={14} color={accentColor} />
                        <S.LabelGroup>
                            <S.ComboName>{comboGrupoTitle}</S.ComboName>
                            <S.TagLabel $isActive={isGroupComplete}>{statusLabel}</S.TagLabel>
                        </S.LabelGroup>
                    </S.Badge>
                );
            })}
        </S.Wrapper>
    );
};

export default SeloCombo;
